import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { useProxyData } from '../contexts/ProxyDataContext';
import { useEmailInbox } from '../contexts/EmailInboxContext';
import { useProxyWorkflowContext } from '../hooks/useProxyWorkflowContext';
import type { ResolvedProxyJob } from '../utils/proxyQueue';
import { pullFollowUpReport } from '../utils/followUpReportPull';
import { buildRedReport, NOT_WORKED, RED_REPORT_TO, RED_REPORT_CC } from '../utils/redReport';
import { PROXY_REPORTS } from '../data/proxyReports';
import { PROXY_SEED_TODAY } from '../data/mockProxyJobs';
import { buildMaterialFollowupTask, materialFollowupResolved, buildProxyIntakeTasks, materialFollowupTaskFor } from '../data/proxyWorkflowRegistry';
import { RecommendedActionChip, BandChip } from '../components/proxy/ProxyChips';
import { POD_MAX_PAGES, POD_MAX_FULL_SET_HOLDERS } from '../utils/proxyDecisioning';
import { SearchInput } from '../components/SearchInput';
import { SortableTh } from '../components/SortableTh';
import { useSort, sortRows } from '../utils/useSort';

// The ICT-1 / ICT-8 deadlines Ace applies to every job, shown above the
// full list so the recommended actions make sense at a glance.
const DEADLINE_RULES: { title: string; body: string }[] = [
  { title: 'Follow up', body: 'Yellow (19 to 30 days to the meeting): chase the solicitor, or the issuer if there isn\'t one. Red, 18 to 11 days to cutoff: check EDGAR or SEDAR first. Found means emails only; not found means a Red follow-up.' },
  { title: '10 days: print on demand', body: `Free at ${POD_MAX_PAGES} pages or less and ${POD_MAX_FULL_SET_HOLDERS} full-set holders or fewer. Over that, the manager decides.` },
  { title: '9 to 5 days: email only', body: 'Cancel the full sets and send emails. At 8 or 9 days, the manager decides first.' },
  { title: 'Under 5 days: abort', body: 'Ace recommends the abort. A person approves it.' },
];

export function ProxyReportDetailPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { setTopbar } = useAppShell();
  const { jobs, updateJob } = useProxyData();
  const ctx = useProxyWorkflowContext();
  const { appendOutbound } = useEmailInbox();
  const [search, setSearch] = useState('');
  const [redSent, setRedSent] = useState(false);
  const { sort, toggle } = useSort({ column: 'daysToVoteCutoff', direction: 'asc' });

  const report = PROXY_REPORTS.find(r => r.id === reportId);

  useEffect(() => {
    setTopbar(
      report ? report.title : 'Report not found',
      report ? `${report.description} This is a static snapshot, not a follow-up queue.` : '',
    );
  }, [setTopbar, report]);

  // ICT-8: today's Red Report, column B filled from what was done today.
  const redReport = useMemo(
    () => (report?.band === 'Red' ? buildRedReport(jobs, ctx.receipts, PROXY_SEED_TODAY) : []),
    [jobs, ctx.receipts, report],
  );
  const redStatus = useMemo(() => new Map(redReport.map(l => [l.job.jobId, l.status])), [redReport]);

  const rows: ResolvedProxyJob[] = useMemo(() => {
    if (!report) return [];
    if (report.band === 'Red') return redReport.map(l => l.job);
    // Same ICT-7 rules as the manual pull — Green is the section ICT-7 deletes.
    const pull = pullFollowUpReport(jobs, ctx.receipts, PROXY_SEED_TODAY);
    const green = pull.excluded.filter(e => e.step === 'green').map(e => e.job);
    if (report.band === 'All') return [...pull.rows.map(r => r.job), ...green];
    return report.band === 'Green' ? green : pull.rows.filter(r => r.band === report.band).map(r => r.job);
  }, [jobs, ctx.receipts, report, redReport]);

  const redReportName = `ICT Red Report ${PROXY_SEED_TODAY.slice(5, 7)}.${PROXY_SEED_TODAY.slice(8, 10)}.${PROXY_SEED_TODAY.slice(0, 4)}`;
  const notWorked = redReport.filter(l => l.status === NOT_WORKED).length;

  function sendRedReport() {
    appendOutbound({
      refKey: 'ict-red-report',
      jobIds: [],
      origin: 'proxy',
      to: RED_REPORT_TO,
      cc: RED_REPORT_CC,
      subject: redReportName,
      body: `Completed Red Report attached (${redReport.length} jobs).\n\n${redReport.map(l => `${l.job.jobId} ${l.job.issuer}: ${l.status}`).join('\n')}`,
    });
    setRedSent(true);
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => r.issuer.toLowerCase().includes(q) || r.jobId.includes(q) || r.cusip.toLowerCase().includes(q));
  }, [rows, search]);

  const sorted = useMemo(() => sortRows(filtered, sort, (r, column) => {
    if (column === 'issuer') return r.issuer;
    if (column === 'daysToVoteCutoff') return r.daysToVoteCutoff;
    if (column === 'action') return r.decision.action;
    return '';
  }), [filtered, sort]);

  const tasks = useMemo(() => buildProxyIntakeTasks(ctx), [ctx]);

  function handleCreateFollowup(job: ResolvedProxyJob) {
    const existing = materialFollowupTaskFor(tasks, job.jobId);
    if (existing) { navigate('/ace', { state: { proxyTask: existing } }); return; }
    updateJob(job.jobId, { materialFollowupRequestedAt: PROXY_SEED_TODAY });
    const task = buildMaterialFollowupTask(job, PROXY_SEED_TODAY);
    navigate('/ace', { state: { proxyTask: task } });
  }

  if (!report) {
    return <div className="px-6 py-6 text-sm text-muted">Report not found.</div>;
  }

  return (
    <div className="h-full overflow-auto px-6 py-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold whitespace-nowrap bg-wash border border-trim rounded-full px-3 py-1 text-copy">
          {rows.length} job{rows.length === 1 ? '' : 's'}
        </span>
        <SearchInput value={search} onChange={setSearch} placeholder="Search job #, issuer, CUSIP…" width={280} />
      </div>

      {report.band === 'All' && (
        <div data-tour="board-rules" className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          {DEADLINE_RULES.map(rule => (
            <div key={rule.title} className="px-4 py-3 rounded-xl border border-trim bg-wash">
              <div className="text-sm font-semibold text-ink">{rule.title}</div>
              <div className="text-xs text-copy mt-1">{rule.body}</div>
            </div>
          ))}
        </div>
      )}

      {report.band === 'Red' && rows.length > 0 && (
        <div data-tour="red-report-send" className="flex items-center justify-between gap-4 px-4 py-3 mb-4 rounded-xl border border-trim bg-wash">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-ink">End of day: send "{redReportName}"</div>
            <div className="text-xs text-muted mt-0.5">
              Ace fills the Status column from what was done on each job today. Goes to the ICT manager, cc documents@, verifications@ and ict@ (ICT-8).
              {notWorked > 0 && ` ${notWorked} job${notWorked === 1 ? '' : 's'} not worked yet.`}
            </div>
          </div>
          {redSent ? (
            <span className="text-xs font-semibold text-[#0F6E56] whitespace-nowrap">Sent ✓</span>
          ) : (
            <button onClick={sendRedReport} className="text-xs font-semibold text-white bg-[#063F5A] rounded-lg px-3 py-2 whitespace-nowrap">
              Send Red Report
            </button>
          )}
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="px-4 py-10 rounded-xl border border-trim bg-wash text-center">
          <div className="text-sm font-medium text-ink mb-0.5">Nothing outstanding</div>
          <div className="text-xs text-muted">{report.band === 'All' ? 'No jobs' : `No ${report.band}-band jobs`} with outstanding material right now.</div>
        </div>
      ) : (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <SortableTh label="Issuer / Job" column="issuer" sort={sort} onSort={toggle} />
              <SortableTh label="Days to cutoff" column="daysToVoteCutoff" sort={sort} onSort={toggle} />
              {report.band === 'All' && <SortableTh label="Band" sort={sort} onSort={toggle} />}
              <SortableTh label="Recommended action" column="action" sort={sort} onSort={toggle} />
              <SortableTh label="Solicitor" sort={sort} onSort={toggle} />
              {report.band === 'Red' && <SortableTh label="Status today" sort={sort} onSort={toggle} />}
              <SortableTh label="" sort={sort} onSort={toggle} />
            </tr>
          </thead>
          <tbody>
            {sorted.map(job => {
              const requested = !!job.materialFollowupRequestedAt && !materialFollowupResolved(job);
              const status = redStatus.get(job.jobId);
              const workedToday = !!status && status !== NOT_WORKED;
              return (
                <tr key={job.jobId} data-tour={`report-row-${job.jobId}`} className="border-b border-trim">
                  <td className="px-2 py-2.5">
                    <div className="text-sm font-medium text-ink">{job.issuer}</div>
                    <div className="text-xs text-muted">Job {job.jobId}</div>
                  </td>
                  <td className="px-2 py-2.5 text-xs text-copy whitespace-nowrap">{job.daysToVoteCutoff}d</td>
                  {report.band === 'All' && <td className="px-2 py-2.5"><BandChip band={job.band} /></td>}
                  <td className="px-2 py-2.5"><RecommendedActionChip decision={job.decision} /></td>
                  <td className="px-2 py-2.5 text-xs text-copy">{job.solicitor || 'Issuer (no solicitor on record)'}</td>
                  {report.band === 'Red' && (
                    <td className={`px-2 py-2.5 text-xs whitespace-nowrap ${workedToday ? 'text-ink font-medium' : 'text-muted'}`}>{status}</td>
                  )}
                  <td className="px-2 py-2.5 text-right">
                    {workedToday ? null : requested && !materialFollowupTaskFor(tasks, job.jobId) ? (
                      <span className="text-xs text-muted whitespace-nowrap">Follow-up requested</span>
                    ) : (
                      <button data-tour={`create-followup-${job.jobId}`} onClick={() => handleCreateFollowup(job)} className="text-xs font-medium text-[#2563EB] whitespace-nowrap">
                        {materialFollowupTaskFor(tasks, job.jobId) ? 'Open task' : 'Create follow-up'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
